import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { t } from '@/i18n';
import type { ProjectOption } from '@ip/shared-types';

/** Sentinel for the "Brak projektu" option (clears the assignment). */
export const NO_PROJECT = '__none__';

interface BulkProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Only active projects are offered — closed ones can't take new invoices. */
  projects: ProjectOption[];
  /** '' = nothing chosen yet, NO_PROJECT = clear, otherwise a project id. */
  selection: string;
  onSelectionChange: (value: string) => void;
  count: number;
  updating: boolean;
  onSubmit: () => void;
}

/**
 * "Przypisz do projektu" bulk dialog, shared by the cost (InvoiceList) and
 * sales (SalesView) lists. Mirrors BulkAssignDialog's controlled pattern:
 * the parent owns the selection and performs the API call on submit.
 */
export function BulkProjectDialog({
  open,
  onOpenChange,
  projects,
  selection,
  onSelectionChange,
  count,
  updating,
  onSubmit,
}: BulkProjectDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('invoices.bulk.projectModalTitle')}</DialogTitle>
        </DialogHeader>
        <p className="text-muted-foreground text-sm">
          {t('invoices.bulk.projectModalDescription', { count })}
        </p>
        <Select value={selection} onValueChange={onSelectionChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t('invoices.bulk.selectProject')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_PROJECT}>
              <span className="text-muted-foreground">{t('invoices.bulk.noProject')}</span>
            </SelectItem>
            {projects
              .filter((project) => project.status === 'active')
              .map((project) => (
                <SelectItem key={project.id} value={project.id}>
                  <span className="flex items-center gap-2">
                    <span
                      className="inline-block size-2 shrink-0 rounded-full border"
                      style={{ backgroundColor: project.color ?? 'transparent' }}
                      aria-hidden="true"
                    />
                    <span className="truncate">{project.name}</span>
                  </span>
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        <DialogFooter>
          <Button variant="outline" disabled={updating} onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button disabled={!selection || updating} onClick={onSubmit}>
            {updating && <Loader2 className="size-4 animate-spin" />}
            {t('invoices.bulk.assignToProject')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
